import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { EMPTY, throwError, of } from 'rxjs';
import { map, catchError, exhaustMap, switchMap } from 'rxjs/operators';
import { EtlService } from '@etl/etl.service';
import { CompanyService } from '@company/company.service';
import * as EtlActions from '@etl/etl.actions';
import { Etl } from '@models';
import { errorTypes } from './etl.reducer';

@Injectable()
export class EtlEffects {
  loadEtls$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.getCollection),
    exhaustMap(() => this.etlService.getEtls().pipe(
      map(etls => EtlActions.collectionLoaded({newCollection: etls})),
      catchError(() => EMPTY)
    ))
  ));

  loadCompanyEtls$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.getCompanyJobs),
    exhaustMap((action) => this.companyService.companyJobs(action.companyId).pipe(
      map(etls => EtlActions.collectionLoaded({newCollection: etls})),
      catchError(() => EMPTY)
    ))
  ));

  addEtl$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.create),
    exhaustMap(action => this.etlService.addEtl(action.row).pipe(
      map((rowAdded: Etl) => EtlActions.rowAdded({row: rowAdded})),
      catchError((err) => of(EtlActions.failRequest(
        {error: err, errorType: errorTypes.creating}
      )))
    ))  // exhaustMap-pipe
  ));

  updateEtl$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.update),
    switchMap(action => this.etlService.updateEtl(action.row).pipe(
      map((rowEdited: Etl) => EtlActions.rowEdited({row: rowEdited})),
      catchError((err) => of(EtlActions.failRequest(
        {error: err, errorType: errorTypes.editing}
      )))
    ))
  ));

  deleteEtl$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.remove),
    switchMap(action => this.etlService.deleteEtl(action.row).pipe(
      map((success: boolean) => EtlActions.rowWasDeleted({row: action.row})),
      catchError((err) => of(EtlActions.failRequest(
        {error: err, errorType: errorTypes.deleting}
      )))
    ))
  ));

  etlTrigger$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.startAction),
    switchMap(action => this.etlService.triggerAction(action.row).pipe(
      map((response: Etl) => EtlActions.startActionSuccess({row: response})),
      catchError((err) => of(EtlActions.failRequest(
        {error: err, errorType: errorTypes.startingJob}
      )))
    ))
  ));

  jobDetails$ = createEffect(() => this.actions$.pipe(
    ofType(EtlActions.jobDetails),
    exhaustMap((action) => this.etlService.loadDetails(action.row).pipe(
      map((etl: Etl) => EtlActions.jobDetailsLoaded({row: etl, finished: true})),
      catchError((err) => of(EtlActions.failRequest(
        {error: err, errorType: errorTypes.fetching}
      )))
    ))
  ));

  constructor(
    private actions$: Actions,
    private etlService: EtlService,
    private companyService: CompanyService,
  ) { }
}
